import {
    SchoolUserAction,
    ADD_SCHOOL_USER
} from '../actions/SchoolUserActions';
import { StoreState } from '../types';
import { Epic, combineEpics } from 'redux-observable';
import { firebaseStore, firebaseStorage } from '../firebase';
import { createTryActionsEpic } from './Common';
import _ from 'lodash';

const hashCode = (s: string) => {
    // tslint:disable-next-line:no-bitwise
    return s.split('').reduce((prev, cur) => (((prev << 5) - prev) + cur.charCodeAt(0)) | 0, 0);
};

const schoolUserDocs = (schoolId: string) =>
    firebaseStore.collection('schools').doc(schoolId).collection('users');

const addSchoolUserDoc = async (schoolId: string, { auth, schoolUsers }: StoreState) => {
    const user = auth.user;
    if (!user || !user.uid) {
        throw new Error('Tried to add an schoolUser without authenticating');
    }

    if (!schoolId) {
        throw new Error('Invalid schoolId');
    }

    const form = schoolUsers.newUserForm;

    if (!form) {
        throw new Error('Invalid form');
    }

    if (!form.email || !form.email.includes('@') || form.email.length < 3) {
        throw new Error(`Invalid email: ${form.email}`);
    }

    const editingSelf = form.email === user.email;

    if (editingSelf) {
        if (!form.active) {
            throw new Error('Tried to mark yourself as Inactive');
        }
        if (!form.roles.find(r => r === 'Admin')) {
            throw new Error('Tried to remove yourself as Admin');
        }
    }

    // map roles onto permissions
    let permissions = {};
    _.forEach(form.roles, role => {
        permissions = { ...permissions, [`is${role}`]: true };
    });

    // save photo to storage and get URL
    let photoUrl = '';
    let savePhotoUrl = false;
    if (form.photoFile && form.photoFile.name) {
        const ext = _.last(form.photoFile.name.split('.'));
        if (ext && form.email) {
            const fileName = hashCode(form.email);
            const ref = firebaseStorage.ref().child(`schools/${schoolId}/images/users/${fileName}.${ext}`);
            const snapshot = await ref.put(form.photoFile);
            photoUrl = await snapshot.ref.getDownloadURL();
            savePhotoUrl = true;
        }
    } else if (form.photoFile === null) {
        savePhotoUrl = true; // delete photoFile if null
    }

    let saveForm = _.omit(form, 'roles', 'photoFile'); // don't want to save these fields
    let doc = {};
    if (!savePhotoUrl) {
        doc = {
            info: { ...saveForm }, 
            permissions
        };
    } else {
        doc = {
            info: { ...saveForm, photoUrl }, 
            permissions
        };
    }

    await schoolUserDocs(schoolId)
        .doc(form.email)
        .set(doc, { merge: true }); // preserves existing photoURL if not present in new doc

    await schoolUserDocs(schoolId)
        .doc(form.email)
        .set(doc, { mergeFields: ['permissions'] }); // normal merge didn't perform any removals of permissions
};

const addSchoolUserEpic: Epic<SchoolUserAction, SchoolUserAction, StoreState> =
    createTryActionsEpic(
        ADD_SCHOOL_USER,
        (a, s) => addSchoolUserDoc(a.value.schoolId, s),
        (a, r) => a.value);

export default combineEpics<SchoolUserAction, SchoolUserAction, StoreState>(addSchoolUserEpic);
